/**
 * 8TrackFit - product page
 *
 * Gallery (stage, thumbs, zoom dialog), quantity stepper and variant switching.
 * Add to cart is handled by tf-home.js, which intercepts any form marked
 * [data-tf-atc] - including Shopify's own product form used here.
 */

(() => {
  function money(cents) {
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ---------------------------------------------------------------- gallery */

  function initGallery(root) {
    const slides = Array.prototype.slice.call(root.querySelectorAll('[data-tf-gal-slide]'));
    const thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-tf-gal-thumb]'));
    const zoomBtn = root.querySelector('[data-tf-gal-zoom]');
    const dialog = root.querySelector('[data-tf-gal-dialog]');
    const dialogImg = root.querySelector('[data-tf-gal-dialog-image]');
    const dialogClose = root.querySelector('[data-tf-gal-dialog-close]');
    if (!slides.length) return null;

    let index = 0;

    function show(i) {
      if (i < 0 || i >= slides.length) return;
      index = i;
      slides.forEach(function (s, n) {
        s.setAttribute('aria-hidden', n === i ? 'false' : 'true');
        if (n !== i) {
          const v = s.querySelector('video');
          if (v && typeof v.pause === 'function') v.pause();
        }
      });
      thumbs.forEach(function (t, n) {
        t.setAttribute('aria-current', n === i ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (t, n) {
      t.addEventListener('click', function () { show(n); });
    });

    if (zoomBtn && dialog && dialogImg) {
      zoomBtn.addEventListener('click', function () {
        const img = slides[index].querySelector('img');
        if (!img) return;
        dialogImg.src = img.currentSrc || img.src;
        dialogImg.alt = img.alt || '';
        if (typeof dialog.showModal === 'function') dialog.showModal();
      });
      if (dialogClose) {
        dialogClose.addEventListener('click', function () { dialog.close(); });
      }
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });
    }

    return {
      show: show,
      showByMediaId: function (id) {
        const i = slides.findIndex(function (s) { return s.dataset.mediaId === String(id); });
        if (i > -1) show(i);
      },
    };
  }

  /* ------------------------------------------------------------ quantity */

  function initQuantity(scope) {
    const input = scope.querySelector('[data-tf-qty-input]');
    if (!input) return;
    scope.querySelectorAll('[data-tf-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const step = Number(btn.dataset.tfQty || 0);
        const next = Math.max(1, (Number(input.value) || 1) + step);
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    input.addEventListener('change', function () {
      if (!input.value || Number(input.value) < 1) input.value = '1';
    });
  }

  /* ------------------------------------------------------------- variants */

  function initBuyBox(buy, gallery) {
    let variants = [];
    try {
      variants = JSON.parse(buy.dataset.variants || '[]');
    } catch (e) {
      variants = [];
    }

    const groups = Array.prototype.slice.call(buy.querySelectorAll('[data-tf-option-group]'));
    const hidden = buy.querySelector('[data-tf-variant-input]');
    const priceEl = buy.querySelector('[data-tf-price]');
    const compareEl = buy.querySelector('[data-tf-compare]');
    const atc = buy.querySelector('[data-tf-atc-button]');
    const atcLabel = buy.querySelector('[data-tf-atc-label]');
    const addLabel = atcLabel ? atcLabel.textContent.trim() : 'Add to cart';

    initQuantity(buy);

    if (!groups.length || !variants.length) return;

    function selected() {
      return groups.map(function (g) {
        const checked = g.querySelector('[data-tf-option]:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return values.every(function (val, i) { return val === null || v.options[i] === val; });
      });
    }

    // grey out option values that cannot be reached from the current selection
    function refreshAvailability(values) {
      groups.forEach(function (g, gi) {
        g.querySelectorAll('[data-tf-option]').forEach(function (input) {
          const probe = values.slice();
          probe[gi] = input.value;
          const match = variants.find(function (v) {
            return probe.every(function (val, i) { return val === null || v.options[i] === val; });
          });
          input.disabled = !match || !match.available;
        });
      });
    }

    function apply() {
      const values = selected();
      const variant = findVariant(values);
      refreshAvailability(values);

      if (!variant) {
        if (atc) {
          atc.disabled = true;
          if (atcLabel) atcLabel.textContent = 'Unavailable';
        }
        return;
      }

      if (hidden) hidden.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (atc) {
        atc.disabled = !variant.available;
        if (atcLabel) atcLabel.textContent = variant.available ? addLabel : 'Sold out';
      }

      if (gallery && variant.featured_media && variant.featured_media.id) {
        gallery.showByMediaId(variant.featured_media.id);
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (e) {
        /* older browsers - not important */
      }
    }

    groups.forEach(function (g) {
      g.addEventListener('change', function (e) {
        if (e.target && e.target.matches('[data-tf-option]')) apply();
      });
    });

    apply();
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    document.querySelectorAll('[data-tf-buy]').forEach(function (buy) {
      if (buy.dataset.tfPdpReady === 'true') return;
      buy.dataset.tfPdpReady = 'true';
      const scope = buy.closest('.tf-pdp') || document;
      const galRoot = scope.querySelector('[data-tf-gal]');
      const gallery = galRoot ? initGallery(galRoot) : null;
      initBuyBox(buy, gallery);
    });

    // galleries with no buy box beside them (unlikely, but harmless)
    document.querySelectorAll('[data-tf-gal]').forEach(function (g) {
      if (g.dataset.tfGalReady === 'true') return;
      g.dataset.tfGalReady = 'true';
      if (!g.closest('.tf-pdp')) initGallery(g);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
